<!-- 
  BaseReadOnlyField - 読み取り専用フィールドコンポーネント (Atom)
  
  フォーム内で編集不可の情報を表示するための再利用可能なコンポーネント。
  ラベルと値を一貫したスタイルで表示します。
  
  使用例:
  <BaseReadOnlyField
    label="ユーザーID"
    :value="formData.userId"
  />
-->
<template>
  <div class="form-group">
    <label v-if="label" class="form-label">
      {{ label }}
      <span v-if="required" class="required">*</span>
    </label>
    <div class="readonly-field">
      <ContentText :color="textColor" :size="textSize">
        {{ displayValue }}
      </ContentText>
    </div>
    <small v-if="description" class="field-description">
      {{ description }}
    </small>
  </div>
</template>

<script setup>
import { computed } from 'vue'
import ContentText from '@/components/atoms/ContentText.vue'

/**
 * BaseReadOnlyField - 読み取り専用フィールドコンポーネント (Atom)
 * 
 * フォーム内で編集不可の情報を表示するための再利用可能なコンポーネント。
 * ラベルと値を一貫したスタイルで表示します。
 * Options APIを使用した実装。
 */

const props = defineProps({
  /**
   * ラベルテキスト
   */
  label: {
    type: String,
    default: ''
  },
  /**
   * 表示する値
   */
  value: {
    type: [String, Number],
    default: ''
  },
  /**
   * 必須フィールドマークを表示するか
   */
  required: {
    type: Boolean,
    default: false
  },
  /**
   * 説明テキスト（フィールドの下に表示）
   */
  description: {
    type: String,
    default: ''
  },
  /**
   * テキストカラー（ContentTextのcolorプロパティ）
   */
  textColor: {
    type: String,
    default: 'default'
  },
  /**
   * テキストサイズ（ContentTextのsizeプロパティ）
   */
  textSize: {
    type: String,
    default: 'md'
  },
  /**
   * 値が空の場合に表示するプレースホルダー
   */
  placeholder: {
    type: String,
    default: '未設定'
  }
})

/**
 * 表示する値（空の場合はプレースホルダーを表示）
 */
const displayValue = computed(() => {
  return props.value || props.placeholder
})
</script>

<style lang="scss" scoped>
.form-group {
  margin-bottom: 1.5rem;
}

.form-label {
  display: block;
  font-weight: 600;
  color: #374151;
  margin-bottom: 0.5rem;
  font-size: 0.875rem;
}

.required {
  color: #ef4444;
  font-weight: 700;
  margin-left: 0.125rem;
}

.readonly-field {
  padding: 0.75rem 1rem;
  background-color: #f8fafc;
  border-radius: 0.375rem;
  border: 1px solid #e2e8f0;
  min-height: 2.5rem;
  display: flex;
  align-items: center;
}

.field-description {
  display: block;
  color: #6b7280;
  font-size: 0.75rem;
  margin-top: 0.25rem;
  line-height: 1.4;
}

// レスポンシブ対応
@media (max-width: 768px) {
  .form-group {
    margin-bottom: 1.25rem;
  }
}
</style>
